import { useTranslation } from 'react-i18next';

// The two closed-question charts are exported images from the survey form and
// live in /public/survey. Their titles/alt text are localized via i18n.
const CHARTS = [
  { key: 'teaching', src: '/survey/status-teaching.png' },
  { key: 'scholarship', src: '/survey/status-scholarship.png' },
];

export default function Survey() {
  const { t } = useTranslation();

  const intro = t('survey.intro', { returnObjects: true });
  const questions = t('survey.questions', { returnObjects: true });
  const conclusions = t('survey.conclusions', { returnObjects: true });
  const headers = t('survey.tableHeaders', { returnObjects: true });

  return (
    <article className="mx-auto max-w-3xl px-6 pb-20 pt-28">
      <header className="mb-10">
        <div className="section-label">{t('survey.label')}</div>
        <h1 className="font-serif text-4xl font-semibold text-cream">
          {t('survey.heading')}
        </h1>
      </header>

      <div className="space-y-6 text-base leading-relaxed text-ink-primary">
        {intro.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      <section className="mt-14 space-y-8">
        <h2 className="font-serif text-2xl font-semibold text-cream">
          {t('survey.closedHeading')}
        </h2>
        {CHARTS.map((c) => (
          <figure
            key={c.key}
            className="rounded-lg border border-gold/20 bg-navy-mid/30 p-4"
          >
            <figcaption className="mb-3 font-serif text-lg font-semibold text-gold-light">
              {t(`survey.charts.${c.key}.title`)}
            </figcaption>
            <img
              src={c.src}
              alt={t(`survey.charts.${c.key}.alt`)}
              className="w-full rounded-md bg-white"
              loading="lazy"
            />
          </figure>
        ))}
      </section>

      <section className="mt-14 space-y-12">
        {questions.map((q, i) => (
          <div key={i}>
            <h2 className="mb-2 font-serif text-2xl font-semibold text-cream">
              <span className="text-gold-light">{i + 1}.</span> {q.title}
            </h2>
            <p className="mb-4 text-sm text-ink-secondary">
              {t('survey.responsesLabel', { n: q.responses })}
            </p>
            <div className="overflow-x-auto rounded-lg border border-gold/20">
              <table className="w-full text-sm">
                <thead className="bg-navy-mid/40 text-left text-ink-secondary">
                  <tr>
                    <th className="px-4 py-2 font-medium">{headers.idea}</th>
                    <th className="px-4 py-2 text-right font-medium">{headers.count}</th>
                    <th className="px-4 py-2 text-right font-medium">{headers.share}</th>
                  </tr>
                </thead>
                <tbody>
                  {q.themes.map((th, j) => (
                    <tr key={j} className="border-t border-white/10">
                      <td className="px-4 py-2 text-ink-primary">{th.label}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-ink-primary">
                        {th.count}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-ink-secondary">
                        {th.share}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-base leading-relaxed text-ink-primary">{q.summary}</p>
          </div>
        ))}
      </section>

      <section className="mt-14">
        <h2 className="mb-4 font-serif text-2xl font-semibold text-cream">
          {t('survey.conclusionsHeading')}
        </h2>
        <div className="space-y-6 text-base leading-relaxed text-ink-primary">
          {conclusions.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </section>
    </article>
  );
}
